@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.laanmanage.feature.settings.tax

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.company.TaxRate
import us.laantech.common.dto.UnitedStatesState
import us.laantech.common.dto.company.ParentCompany
import us.laantech.common.ui.DataTableView
import us.laantech.common.ui.DateTimeEntryModal
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.util.to5dp
import us.laantech.common.util.tryFormatAsDateTime
import us.laantech.common.api.KtorClient
import us.laantech.laanmanage.feature.scope.ParentCompanyScope
import kotlin.time.Clock




data class TaxSetupUiState(
    val taxRates: List<TaxRate> = emptyList(),

    // Modal
    val showNewTaxRateModal: Boolean = false,
    val taxRateTextFieldState: TextFieldState = TextFieldState(""),
    val taxEffectiveDate: Long = Clock.System.now().toEpochMilliseconds(),
    val showDatePicker: Boolean = false,
    val selectedState: UnitedStatesState? = null,
    val parentCompany: ParentCompany? = null,
)


@Composable
fun TaxSetupView(

    viewModel: TaxSetupViewModel = viewModel(
        factory = viewModelFactory {
            initializer { TaxSetupViewModel() }
        }
    )
) {

    val uiState by viewModel.uiState.collectAsState()

    ParentCompanyScope(requireBranch = false) { parentCompany, _ ->

        LaunchedEffect(parentCompany) {
            viewModel.setParentCompany(parentCompany)
            viewModel.loadTaxRates()
        }

        Column(Modifier.fillMaxSize()) {
            Text("Tax Rates")

            Button({ viewModel.toggleNewTaxRateModal() }) {
                Text("Create Tax Rate")
            }

            DataTableView<TaxRate>(
                modifier = Modifier.fillMaxSize(),
                rows = uiState.taxRates,
            ) {
                column("State") {
                    Text(it.usState?.displayName ?: "Unknown", modifier = Modifier.weight(1f))
                }
                column("Tax Rate") {
                    Text(
                        "${(it.taxRates5dp ?: 0L) / 100000.0} %",
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center
                    )
                }
                column("Start Date") {
                    Text(
                        it.startAt?.tryFormatAsDateTime() ?: "Invalid Date",
                        modifier = Modifier.weight(1f),
                        textAlign = TextAlign.Center
                    )
                }

            }


        }

        if (uiState.showNewTaxRateModal) {
            TaxRateModal(viewModel)
        }

    }
}

@Composable
fun TaxRateModal(viewModel: TaxSetupViewModel) {

    val uiState by viewModel.uiState.collectAsState()

    var showStartDatePicker by remember { mutableStateOf(false) }

    BasicAlertDialog({ viewModel.toggleNewTaxRateModal() }) {
        Surface(shape = MaterialTheme.shapes.large) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                Text("Create Tax Rate")

                DropdownMenu(
                    label = "State",
                    items = UnitedStatesState.entries,
                    selectedItem = uiState.selectedState,
                    itemLabel = { it.displayName },
                    onItemSelected = { selectedState ->
                        viewModel.onSelectedStateChange(selectedState)
                    },
                    noneLabel = "Select State"
                )

                Row(verticalAlignment = Alignment.CenterVertically) {
                    TextField(
                        uiState.taxRateTextFieldState,
                        label = { Text("Tax Rate Percent") },
                        textStyle = LocalTextStyle.current.copy(textAlign = TextAlign.End),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                    )
                    Text(
                        " %",
                        style = LocalTextStyle.current.copy(
                            fontSize = MaterialTheme.typography.titleLarge.fontSize,
                        )
                    )
                }
                Button({ showStartDatePicker = true }) {
                    Text("Start Date ${uiState.taxEffectiveDate.tryFormatAsDateTime()}")
                }

                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
                    Button({ viewModel.createTaxRate() }) {
                        Text("Create")
                    }

                    Button({ viewModel.toggleNewTaxRateModal() }) {
                        Text("Cancel")
                    }
                }
            }

            if (showStartDatePicker) {
                DateTimeEntryModal(
                    initialEpochMillis = uiState.taxEffectiveDate,
                    title = "Select Tax Start Date",
                    onDismiss = {
                        showStartDatePicker = false
                    },
                    onConfirm = { epochMillis ->
                        viewModel.updateTaxEffectiveDate(epochMillis)
                        showStartDatePicker = false
                    },
                )
            }
        }
    }


}


class TaxSetupViewModel : ViewModel() {

    val _uiState = MutableStateFlow(TaxSetupUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)

    fun loadTaxRates() {
        scope.launch {
            val response = ktClient.get("/backoffice/settings/tax-rates/list") {
                parameter("parentCompanyUuid", uiState.value.parentCompany?.uuid)
            }
            val taxRates = response.body<List<TaxRate>>()
            _uiState.value = _uiState.value.copy(taxRates = taxRates)
        }
    }

    fun createTaxRate() {
        scope.launch {

            // User enters as percent, convert to long with precision 5 digits for API
            val taxRate5dp = uiState.value.taxRateTextFieldState.text.toString().to5dp()

            val body = TaxRate(
                taxRates5dp = taxRate5dp,
                startAt = uiState.value.taxEffectiveDate,
                usState = uiState.value.selectedState
            )

            print("createTaxRate parentCompany: ${uiState.value.parentCompany}")

            val response = ktClient.post("/backoffice/settings/set/tax-rate") {
                contentType(ContentType.Application.Json)
                parameter("parentCompanyUuid", uiState.value.parentCompany?.uuid)
                setBody(body)
            }

            println("TaxSetupViewModel createTaxRate response: ${response.bodyAsText()}")

            if (response.status.isSuccess()) {
                _uiState.update { it.copy(showNewTaxRateModal = false) }
                loadTaxRates()
            }

        }
    }

    fun toggleNewTaxRateModal() {
        _uiState.update { it.copy(showNewTaxRateModal = !it.showNewTaxRateModal) }
    }

    fun updateTaxEffectiveDate(epochMillis: Long) {
        _uiState.update { it.copy(taxEffectiveDate = epochMillis) }
    }

    fun onSelectedStateChange(selectedState: UnitedStatesState?) {
        _uiState.update { it.copy(selectedState = selectedState) }
    }

    fun setParentCompany(parentCompany: ParentCompany) {
        _uiState.update { it.copy(parentCompany = parentCompany) }
    }

}